import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo } from "react";

import useCookies from "../../hooks/useCookies";
import { getAgencyDetails } from "../../store/actions/agencyActions";
import {
  destroySession,
  getAgentById,
  initApiToken,
  initPosSession,
} from "../../store/actions/sessionActions";
import {
  updateAgency,
  updateAgent,
  updateAgentName,
  updateSessionKey,
  updateSource,
  updateToken,
} from "../../store/slices/sessionSlice";
import { useTypedDispatch, useTypedSelector } from "../../store/store";
import LoadingContainer from "../containers/LoadingContainer";

const checkTokenValidity = (
  token: string | null,
  options: {
    explicitExp?: string | null;
    bufferSeconds?: number;
  } = {},
): boolean => {
  if (!token) {
    return false;
  }

  const { explicitExp, bufferSeconds = 0 } = options;
  const now = dayjs();

  if (explicitExp) {
    const exp = dayjs(explicitExp);

    return exp.subtract(bufferSeconds, "seconds").isAfter(now);
  }

  try {
    const decoded: { exp: number } = jwtDecode(token);
    const nowUnix = now.unix();

    return decoded.exp > nowUnix + bufferSeconds;
  } catch (e) {
    return false;
  }
};

function SessionGuard({ children }: React.PropsWithChildren) {
  const dispatch = useTypedDispatch();
  const { getCookie, setCookie } = useCookies();

  const { isInitialized, name, agent, agency, sessionKey, token, source } =
    useTypedSelector((state) => state.session);

  const cookieToken = getCookie("token");
  const cookieTokenExp = getCookie("token_exp");
  const cookieSource = getCookie("source");

  const cookieAgency = getCookie("active_agency") || getCookie("agency");
  const cookieAgent = getCookie("active_agent") || getCookie("agent");

  const cookieSessionKeyExp =
    getCookie("active_sessionKey_exp") || getCookie("sessionKey_exp");

  const cookieSessionKey =
    getCookie("active_sessionKey") || getCookie("sessionKey");

  const cookieAgentName =
    getCookie("active_agentName") || getCookie("agentName");

  const isTokenValid = useMemo(
    () => checkTokenValidity(cookieToken, { explicitExp: cookieTokenExp }),
    [cookieToken, cookieTokenExp],
  );

  const isSessionKeyValid = useMemo(
    () =>
      checkTokenValidity(cookieSessionKey, {
        explicitExp: cookieSessionKeyExp,
      }),
    [cookieSessionKey, cookieSessionKeyExp],
  );

  const initToken = () => {
    if (isTokenValid) {
      dispatch(updateToken(cookieToken));
    } else {
      dispatch(initApiToken());
    }
  };

  const initAgentSession = () => {
    dispatch(updateSessionKey(cookieSessionKey));
    dispatch(updateAgentName(cookieAgentName));
    dispatch(updateAgency(cookieAgency));
    dispatch(updateAgent(cookieAgent));

    setCookie("sessionKey_exp", dayjs().add(3600, "seconds").format());
  };

  useEffect(initToken, []);

  useEffect(() => {
    if (!cookieSessionKey || (cookieSessionKey && !isSessionKeyValid)) {
      dispatch(destroySession());
    } else {
      initAgentSession();
    }
  }, []);

  useEffect(() => {
    if (agent) {
      dispatch(getAgentById(agent));
    }
  }, [agent]);

  useEffect(() => {
    if (agent && agency && !cookieAgent && !cookieAgency) {
      dispatch(initPosSession({ silent: false }));
    }
  }, [agent, agency]);

  useEffect(() => {
    if (agency) {
      dispatch(getAgencyDetails(agency));
    }
  }, [agency]);

  useEffect(() => {
    if (token && !isTokenValid) {
      setCookie("token", token);
      setCookie("token_exp", dayjs().add(3600, "seconds").format());
    }

    if (token) {
      const decodedToken: { client: string } = jwtDecode(token);

      dispatch(updateSource(decodedToken.client));
    }
  }, [token, isTokenValid]);

  useEffect(() => {
    if (source && !cookieSource) {
      setCookie("source", source);
    }

    if (name && !cookieAgentName) {
      setCookie("agentName", name);
    }

    if (sessionKey && !cookieSessionKey) {
      setCookie("sessionKey", sessionKey);
    }

    if (agency && !cookieAgency) {
      setCookie("agency", agency);
    }

    if (agent && !cookieAgent) {
      setCookie("agent", agent);
    }
  }, [source, sessionKey, name, agency, agent]);

  return (
    <LoadingContainer isLoading={!isInitialized}>{children}</LoadingContainer>
  );
}

export default SessionGuard;
